/**
 * Модал «Рабочие места из структуры компании» (canon service-sout, p3-sout-workplaces §2,
 * D2) — открывается по кнопке «Из структуры компании» на шаге «Рабочие места» (workplaces-
 * step.tsx). Визуальный эталон — макет `#empModal`/`renderEmpList()` + скриншот ТЗ
 * `Правки_2_изображения/02-выбор-из-структуры.png`: заголовок/подпись, поле поиска,
 * список по подразделениям (пустые группы скрыты), чекбокс+ФИО+должность(+сведения о
 * сменщике), «Уже в заказе»+disabled, счётчик «Выбрано: N», кнопки «Отмена»/«Добавить
 * выбранных», пояснение про сменщиков под списком — тексты дословно по §2.1.
 *
 * p3-critical-fixes (№3б): «решение заказчика A» (вакантные рабочие места в диалоге) ОТМЕНЕНО
 * РП — модал показывает ТОЛЬКО сотрудников. Рабочее место без сотрудника больше не попадает в
 * заказ СОУТ ни одним путём (инвариант «нет строки заказа СОУТ без сотрудника»).
 *
 * Постраничная выдача (issue #15): список — страница из 20 сотрудников (`limit`/`offset`,
 * `total`), навигация — общий `Pagination` под списком. Поиск по ФИО ИЛИ ДОЛЖНОСТИ —
 * серверный (`q` + `searchPosition=true`): отбор загруженной страницы на фронте не находил
 * бы сотрудника за её пределами. Выбор хранится по id и переживает смену страницы, поиска и
 * фильтра — «Добавить выбранных» отправляет весь набор, включая скрытых поиском.
 *
 * «Сведения о сменщике» (§2.1 п.3, Opus-ревью Волны 5 HIGH-2): используется АВТОРИТЕТНОЕ
 * поле `Employee.shiftPartners` (person_shift_partners, то же, что и `employee-card.tsx`/
 * `workplace-card.tsx`) — НЕ эвристика «первый по порядку списка = основной». Бэкенд
 * (`implListEmployees`) дозаполняет `shiftPartners` для `isShift=true` строк роster'а тем же
 * `store.ListShiftPartners`, что и карточка сотрудника. Для `e.isShift && e.shiftPartners.length
 * > 0` подпись — «сменщик <Фамилия И.О.>» по ПЕРВОМУ партнёру (упрощённая номинативная форма
 * вместо родительного падежа со скриншота — деклинация фамилий программно не делается,
 * отклонение зафиксировано в отчёте волны).
 */
import { useEffect, useMemo, useState } from 'react'
import { cn } from '@/lib/utils'
import {
  companyApi,
  soutApi,
  type Employee,
  type SoutBulkRejection,
  type SoutLine,
} from '@/lib/api'
import { getErrorMessage } from '@/lib/errors'
import { Dialog, DialogContent } from '@/design-system/components/primitives/dialog'
import { Card } from '@/design-system/components/layout/card'
import { Button } from '@/design-system/components/primitives/button'
import { Input } from '@/design-system/components/primitives/input'
import { Checkbox } from '@/design-system/components/primitives/checkbox'
import { Badge } from '@/design-system/components/primitives/badge'
import { Alert } from '@/design-system/components/feedback/alert'
import { Pagination } from '@/design-system/components/data/pagination'
import { PAGE_SIZE, pageCount } from '@/lib/pagination'

/** Текст подписи диалога (§2.1) — дословно. */
export const STRUCTURE_MODAL_TITLE = 'Рабочие места из структуры компании'
export const STRUCTURE_MODAL_DESCRIPTION =
  'Отметьте сотрудников — подразделение, должность и ФИО подставятся в карточки рабочих мест. Список — из пульта «Охрана труда».'
export const STRUCTURE_MODAL_SEARCH_PLACEHOLDER = 'Поиск по ФИО или должности'
export const STRUCTURE_MODAL_SHIFT_HINT =
  'Сменщики работают на одном рабочем месте, но в анкете каждый указывается отдельной строкой со своим СНИЛС. Если СНИЛС указан в структуре компании — подставится сам, иначе заполните его в карточке рабочего места.'

interface EmployeeRow {
  id: string
  department: string
  position: string
  fullName: string
  shiftNote?: string
  alreadyInOrder: boolean
  /** Исходная запись — выбор хранит её целиком (см. `selectedPersons`, issue #15). */
  employee: Employee
}

/** «Фамилия И.О.» из полного ФИО (упрощённо, без склонения падежа — см. докстринг файла). */
function shortName(fullName: string): string {
  const parts = fullName.trim().split(/\s+/).filter(Boolean)
  if (parts.length < 2) return fullName
  const [last, first, patronymic] = parts
  const initials = [first, patronymic]
    .filter(Boolean)
    .map((p) => `${p.charAt(0).toUpperCase()}.`)
    .join(' ')
  return initials ? `${last} ${initials}` : last
}

export interface StructureModalProps {
  orderId: string
  /** Текущие строки заказа — для вычисления «Уже в заказе» без лишнего запроса. */
  lines: SoutLine[]
  onClose: () => void
  /** Успешно добавленные строки (может быть непустым И при частичной неудаче). */
  onAdded: (added: SoutLine[]) => void
}

export function StructureModal({ orderId, lines, onClose, onAdded }: StructureModalProps) {
  /** Сотрудники ТЕКУЩЕЙ страницы (issue #15): весь ростер в диалог больше не грузится. */
  const [employees, setEmployees] = useState<Employee[]>([])
  const [total, setTotal] = useState(0)
  const [page, setPage] = useState(1)
  const [loading, setLoading] = useState(true)
  const [loadError, setLoadError] = useState<string | null>(null)
  const [search, setSearch] = useState('')
  // Поиск уходит на сервер с дебаунсом (эталон ростера/`/team/orders`): фильтрация
  // загруженной страницы на фронте не нашла бы сотрудника за её пределами.
  const [debouncedSearch, setDebouncedSearch] = useState('')
  /**
   * Выбор хранится по id и ПЕРЕЖИВАЕТ смену страницы и поиска (issue #15 п.5). Значение —
   * сам сотрудник: строка отклонения (§2.2) должна называть ФИО и тогда, когда выбранный
   * сотрудник сейчас не на экране (другая страница, скрыт поиском).
   */
  const [selectedPersons, setSelectedPersons] = useState<Map<string, Employee>>(new Map())
  const [submitting, setSubmitting] = useState(false)
  const [submitError, setSubmitError] = useState<string | null>(null)
  const [rejections, setRejections] = useState<SoutBulkRejection[]>([])

  // Любое изменение запроса открывает первую страницу; очистка поля применяется сразу.
  useEffect(() => {
    const next = search.trim()
    if (next === '') {
      setDebouncedSearch('')
      setPage(1)
      return
    }
    const t = setTimeout(() => {
      setDebouncedSearch(next)
      setPage(1)
    }, 300)
    return () => clearTimeout(t)
  }, [search])

  useEffect(() => {
    const controller = new AbortController()
    setLoading(true)
    setLoadError(null)
    companyApi
      .listEmployees(
        {
          activeOnly: true,
          q: debouncedSearch || undefined,
          // Поле подписано «Поиск по ФИО или должности» — должность ищет сервер (issue #15).
          searchPosition: debouncedSearch ? true : undefined,
          limit: PAGE_SIZE,
          offset: (page - 1) * PAGE_SIZE,
        },
        controller.signal,
      )
      .then((emps) => {
        // Ответ устаревшего запроса не перезаписывает актуальную страницу (AbortController).
        if (controller.signal.aborted) return
        // Страница «уехала» за пределы результатов (поиск сузил набор) — на первую.
        if (emps.items.length === 0 && page > 1) {
          setPage(1)
          return
        }
        setEmployees(emps.items)
        setTotal(emps.total)
      })
      .catch((err) => {
        if (!controller.signal.aborted) {
          setLoadError(getErrorMessage(err, 'Не удалось загрузить список сотрудников'))
        }
      })
      .finally(() => {
        if (!controller.signal.aborted) setLoading(false)
      })
    return () => controller.abort()
  }, [debouncedSearch, page])

  const personIdsInOrder = useMemo(
    () => new Set(lines.map((l) => l.personId).filter((v): v is string => !!v)),
    [lines],
  )

  const groups = useMemo(() => {
    const order: string[] = []
    const byDept = new Map<string, EmployeeRow[]>()
    function push(department: string, row: EmployeeRow) {
      const key = department.trim() || 'Без подразделения'
      if (!byDept.has(key)) {
        byDept.set(key, [])
        order.push(key)
      }
      byDept.get(key)!.push(row)
    }
    for (const e of employees) {
      // Opus-ревью Волны 5 (HIGH-2): АВТОРИТЕТНОЕ `e.shiftPartners` (person_shift_partners,
      // дозаполняется бэком для isShift-строк роster'а) — не эвристика по порядку списка.
      const shiftNote =
        e.isShift && e.shiftPartners.length > 0
          ? `сменщик ${shortName(e.shiftPartners[0].fullName)}`
          : undefined
      push(e.departmentName, {
        id: e.id,
        department: e.departmentName,
        position: e.position,
        fullName: e.fullName,
        shiftNote,
        alreadyInOrder: personIdsInOrder.has(e.id),
        employee: e,
      })
    }
    return order.map((department) => ({ department, rows: byDept.get(department)! }))
  }, [employees, personIdsInOrder])

  const selectedCount = selectedPersons.size

  function toggleEmployee(employee: Employee) {
    setSelectedPersons((prev) => {
      const next = new Map(prev)
      if (next.has(employee.id)) next.delete(employee.id)
      else next.set(employee.id, employee)
      return next
    })
  }

  /** Человекочитаемая подпись для строки отклонения (§2.2 «показать, кого и почему»). */
  function rejectionLabel(r: SoutBulkRejection): string {
    if (r.personId) {
      // Отклонённый мог быть выбран на другой странице — имя берём из выбора, не из страницы.
      const emp = selectedPersons.get(r.personId) ?? employees.find((e) => e.id === r.personId)
      return `${emp ? emp.fullName : r.personId}: ${r.reason}`
    }
    return r.reason
  }

  async function submit() {
    setSubmitError(null)
    setSubmitting(true)
    try {
      const result = await soutApi.bulkAddFromStructure(orderId, {
        // Весь выбор со ВСЕХ страниц, включая временно скрытых поиском (issue #15 п.5).
        personIds: Array.from(selectedPersons.keys()),
      })
      onAdded(result.added)
      if (result.rejected.length === 0) {
        onClose()
        return
      }
      // §2.2: частичная неудача — диалог НЕ закрывается, показываем кого и почему не добавили;
      // успешно добавленных снимаем из выбора (повторное «Добавить выбранных» не должно
      // пытаться добавить их снова — они уже «Уже в заказе»).
      setRejections(result.rejected)
      setSelectedPersons((prev) => {
        const next = new Map(prev)
        for (const l of result.added) if (l.personId) next.delete(l.personId)
        return next
      })
    } catch (err) {
      setSubmitError(getErrorMessage(err, 'Не удалось добавить рабочие места'))
    } finally {
      setSubmitting(false)
    }
  }

  return (
    <Dialog
      open
      onOpenChange={(next) => {
        if (!next) onClose()
      }}
    >
      <DialogContent title={STRUCTURE_MODAL_TITLE} className="max-w-2xl">
        <Card
          header={STRUCTURE_MODAL_TITLE}
          footer={
            <div className="flex flex-wrap items-center justify-between gap-3">
              <span className="text-txt-secondary">Выбрано: {selectedCount}</span>
              <div className="flex gap-2">
                <Button variant="secondary" onClick={onClose} disabled={submitting}>
                  Отмена
                </Button>
                <Button onClick={() => void submit()} disabled={submitting || selectedCount === 0}>
                  Добавить выбранных
                </Button>
              </div>
            </div>
          }
        >
          <p className="mb-3 text-txt-secondary">{STRUCTURE_MODAL_DESCRIPTION}</p>
          <Input
            placeholder={STRUCTURE_MODAL_SEARCH_PLACEHOLDER}
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="mb-3"
          />

          {rejections.length > 0 ? (
            <Alert variant="danger" className="mb-3" title="Не удалось добавить">
              <ul className="list-disc pl-5">
                {rejections.map((r, i) => (
                  <li key={`${r.personId ?? i}-${i}`}>{rejectionLabel(r)}</li>
                ))}
              </ul>
            </Alert>
          ) : null}
          {submitError ? (
            <Alert variant="danger" className="mb-3">
              {submitError}
            </Alert>
          ) : null}
          {loadError ? (
            <Alert variant="danger" className="mb-3">
              {loadError}
            </Alert>
          ) : null}

          {loading ? (
            <p className="py-6 text-center text-txt-secondary">Загрузка…</p>
          ) : groups.length === 0 ? (
            <p className="py-6 text-center text-txt-secondary">Ничего не найдено</p>
          ) : (
            <div className="max-h-96 overflow-y-auto pr-1">
              {groups.map((g) => (
                <div key={g.department} className="mb-4 last:mb-0">
                  <p className="mb-2 text-12 font-semibold uppercase tracking-wide text-txt-secondary">
                    {g.department}
                  </p>
                  <div className="flex flex-col divide-y divide-line">
                    {g.rows.map((row) => (
                      <label
                        key={row.id}
                        className={cn(
                          'flex cursor-pointer items-center gap-3 py-2.5',
                          row.alreadyInOrder && 'cursor-not-allowed opacity-60',
                        )}
                      >
                        <Checkbox
                          checked={selectedPersons.has(row.id)}
                          disabled={row.alreadyInOrder}
                          onCheckedChange={() => toggleEmployee(row.employee)}
                        />
                        <div className="min-w-0 flex-1">
                          <p className="text-14 font-medium text-txt">{row.fullName}</p>
                          <p className="text-12 text-txt-secondary">
                            {row.position}
                            {row.shiftNote ? ` · ${row.shiftNote}` : ''}
                          </p>
                        </div>
                        {row.alreadyInOrder ? <Badge variant="gray">уже в заказе</Badge> : null}
                      </label>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          )}

          {/*
            Навигация — под списком, ПЕРЕД кнопками действия (кнопки живут в футере Card);
            переключение страницы диалог не закрывает. При одной странице компонент скрыт.
          */}
          <Pagination page={page} totalPages={pageCount(total)} onChange={setPage} />

          <p className="mt-3 text-13 text-txt-tertiary">{STRUCTURE_MODAL_SHIFT_HINT}</p>
        </Card>
      </DialogContent>
    </Dialog>
  )
}
